<template>
  <v-snackbar
    v-model="show"
    :timeout="timeout"
    :color="toastColor"
    :location="location"
    :variant="variant"
    class="custom-toast"
    elevation="6"
  >
    <div class="toast-content">
      <v-icon 
        v-if="icon" 
        :color="iconColor" 
        class="toast-icon"
        :size="iconSize"
      >
        {{ icon }}
      </v-icon>
      
      <div class="toast-text">
        <div v-if="title" class="toast-title">{{ title }}</div>
        <div class="toast-message">{{ message }}</div>
      </div>
      
      <v-btn
        v-if="showCloseButton"
        icon
        size="small"
        variant="text"
        @click="close"
        class="toast-close-btn"
      >
        <v-icon size="18" :color="iconColor">mdi-close</v-icon>
      </v-btn>
    </div>
    
    <template v-slot:actions v-if="actions && actions.length > 0">
      <v-btn
        v-for="action in actions"
        :key="action.text"
        variant="text"
        size="small"
        :color="action.color || 'white'"
        @click="handleAction(action)"
        class="toast-action-btn"
      >
        {{ action.text }}
      </v-btn>
    </template>
  </v-snackbar>
</template>

<script>
export default {
  name: 'ToastBar',
  props: {
    // Core props
    modelValue: {
      type: Boolean,
      default: false
    },
    message: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'info',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    
    // Styling props
    timeout: {
      type: Number,
      default: 4000
    },
    location: {
      type: String,
      default: 'top right',
      validator: (value) => [
        'top', 'top right', 'top left', 'top center',
        'bottom', 'bottom right', 'bottom left', 'bottom center',
        'center'
      ].includes(value)
    },
    variant: {
      type: String,
      default: 'elevated'
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    
    // Action buttons
    actions: {
      type: Array,
      default: () => []
    },
    
    // Custom styling
    customColor: {
      type: String,
      default: ''
    },
    customIcon: {
      type: String,
      default: ''
    }
  },
  
  emits: ['update:modelValue', 'close', 'action'],
  
  computed: {
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
        if (!value) {
          this.$emit('close');
        }
      }
    },
    
    toastColor() {
      if (this.customColor) return this.customColor;
      
      const colors = {
        success: '#00D2AF',
        error: '#FF5252',
        warning: '#DABD47', 
        info: '#2196F3'
      };
      return colors[this.type];
    },
    
    icon() {
      if (this.customIcon) return this.customIcon;
      
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        warning: 'mdi-alert',
        info: 'mdi-information'
      };
      return icons[this.type];
    },
    
    iconColor() {
      return 'white';
    },
    
    iconSize() {
      return this.title ? '24' : '20';
    }
  },
  
  methods: {
    close() {
      this.show = false;
    },
    
    handleAction(action) {
      this.$emit('action', action);
      if (action.callback && typeof action.callback === 'function') {
        action.callback();
      }
      if (action.closeOnClick !== false) {
        this.close();
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

/* Custom Toast Styling */
:deep(.v-snackbar__wrapper) {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

:deep(.v-snackbar__content) {
  padding: 0 !important;
  font-family: 'Poppins', sans-serif !important;
}

.toast-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  min-height: auto;
}

.toast-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.toast-text {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: white;
  margin-bottom: 4px;
  line-height: 1.2;
}

.toast-message {
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.4;
  word-wrap: break-word;
}

.toast-close-btn {
  flex-shrink: 0;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.toast-close-btn:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.toast-action-btn {
  font-family: 'Poppins', sans-serif !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .toast-content {
    padding: 14px 16px;
    gap: 10px;
  }
  
  .toast-title {
    font-size: 15px;
  }
  
  .toast-message {
    font-size: 13px;
  }
  
  :deep(.v-snackbar__wrapper) {
    margin: 8px !important;
    border-radius: 8px !important;
  }
}

/* Animation improvements */
:deep(.v-snackbar--variant-elevated) {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Custom positioning adjustments */
:deep(.v-overlay__content) {
  margin: 16px;
}

@media (max-width: 600px) {
  :deep(.v-overlay__content) {
    margin: 8px;
  }
}

/* Success variant custom styling */
.custom-toast:deep(.v-snackbar--variant-elevated) {
  border-left: 4px solid rgba(255, 255, 255, 0.3);
}
</style>